import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Doctor, NameFormat } from 'src/API';
import { ClinicService } from 'src/app/core/api/clinic.service';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { Patient, PatientService } from 'src/app/core/api/patient.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { AssessmentBody } from 'src/app/econsult/assessment-body/assessment-body.model';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { PDFType } from 'src/app/shared/dry-eye-pdf-export/dry-eye-pdf-export.component';
import {
  ButtonAppearances,
  DropdownArrowDirections,
  ExportDestination,
  ExportDestinations
} from 'src/app/shared/dry-eye-pdf-export/pdf-export-button/pdf-export-button.component';
import { AssessmentPdfSendService } from 'src/app/shared/dry-eye-pdf-export/assessment-pdf-send.service';
import { SideSheetOptions } from 'src/app/shared/side-sheet/side-sheet.service';
import { isReEnrollmentEligible } from 'src/app/patients/patient-app-actions.util';
import { AssessmentRow, LinkedQuestionnaireRow } from './assessment-column.model';
import {
  buildNarrativeSections,
  buildPlanTextRows,
  buildTreatmentGroups,
  NarrativeSection,
  PlanTextRow,
  readSignoffField,
  TreatmentGroup
} from './assessment-detail.model';

/**
 * What the body hands back when it closes. Navigation and compare state both live in the column,
 * not here: a component created by the dialog resolves ActivatedRoute to the ROOT route, so a
 * relative router.navigate from inside the sheet would silently target the wrong outlet.
 */
export type AssessmentDetailAction = 'openAssessment' | 'compare';

@Component({
  selector: 'csi-assessment-detail-sheet',
  templateUrl: './assessment-detail-sheet.component.html',
  styleUrls: ['./assessment-detail-sheet.component.scss']
})
export class AssessmentDetailSheetComponent implements OnDestroy {
  static readonly sideSheetOptions: SideSheetOptions = { width: '30rem' };

  public readonly ButtonAppearances = ButtonAppearances;
  public readonly DropdownArrowDirections = DropdownArrowDirections;
  public readonly NameFormat = NameFormat;
  public readonly recordTypeLabel: string;
  public readonly narrativeSections: NarrativeSection[];
  public readonly doctorNotes: string;
  public readonly treatmentGroups: TreatmentGroup[];
  public readonly planTextRows: PlanTextRow[];
  public readonly signedOffDoctor$: Observable<Partial<Doctor>>;
  public readonly signedOffAt: string;
  public readonly hasSeparateUpdatedDate: boolean;
  public expandedSections: { [sectionLabel: string]: boolean } = {};
  public readonly isExportingPdf$: Observable<boolean>;
  public readonly isPatientAppActive$: Observable<boolean>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public assessmentRow: AssessmentRow,
    private sheetReference: MatDialogRef<AssessmentDetailSheetComponent, AssessmentDetailAction>,
    private patientService: PatientService,
    private clinicService: ClinicService,
    private appStateService: AppStateService,
    private clinicSetupService: ClinicSetupService,
    private assessmentPdfSendService: AssessmentPdfSendService
  ) {
    this.recordTypeLabel = assessmentRow.methodLabel ? assessmentRow.methodLabel : 'Assessment';
    this.hasSeparateUpdatedDate =
      !!assessmentRow.updatedAt &&
      this.toCalendarDay(assessmentRow.updatedAt) !== this.toCalendarDay(assessmentRow.createdAt);
    this.isPatientAppActive$ = this.appStateService.isPatientAppActive$;
    this.isExportingPdf$ = this.assessmentPdfSendService.isExporting$;

    const assessmentBody = safeJsonParse(assessmentRow.assessment.body) as AssessmentBody;
    const assessmentForm = assessmentBody && assessmentBody.assessmentForm;
    const impressions = assessmentForm ? assessmentForm.impressions : '';

    this.narrativeSections = buildNarrativeSections(impressions);
    this.doctorNotes = assessmentForm ? assessmentForm.doctorNotes : '';
    this.treatmentGroups = buildTreatmentGroups(assessmentForm);
    this.planTextRows = buildPlanTextRows(assessmentForm);

    const signoff = assessmentBody && assessmentBody.interactionSignoffGroup;
    const signoffValue: any = signoff ? (signoff as any).controls || signoff : null;
    this.signedOffDoctor$ = this.resolveSignedOffDoctor(
      readSignoffField(signoffValue, 'doctorSigning')
    );
    this.signedOffAt = readSignoffField(signoffValue, 'dateAndTime');
  }

  private toCalendarDay(dateValue: string): string {
    if (!dateValue) {
      return '';
    }
    return new Date(dateValue).toDateString();
  }

  private resolveSignedOffDoctor(signedOffDoctorId: string): Observable<Partial<Doctor>> {
    if (!signedOffDoctorId) {
      return of(null);
    }
    return this.clinicService.getClinicStaff(this.clinicSetupService.clinicId).pipe(
      map(clinicStaff => clinicStaff.find(staff => staff.id === signedOffDoctorId) || null),
      catchError(() => of(null))
    );
  }

  public toggleSection(sectionLabel: string): void {
    this.expandedSections = {
      ...this.expandedSections,
      [sectionLabel]: !this.expandedSections[sectionLabel]
    };
  }

  public trackByNarrativeLabel(index: number, narrativeSection: NarrativeSection): string {
    return narrativeSection.label;
  }


  public trackByTreatmentGroup(index: number, treatmentGroup: TreatmentGroup): string {
    return treatmentGroup.label;
  }

  public get hasClinicalNarrative(): boolean {
    return this.assessmentRow.hasClinicalNarrative;
  }

  public get patient(): Patient {
    return this.patientService.patient;
  }

  public get patientHasEmail(): boolean {
    const patient = this.patientService.patient;
    return !!patient && !!patient.email;
  }

  public get patientHasPostalCode(): boolean {
    const patient = this.patientService.patient;
    return !!patient && !!patient.postalCode;
  }

  public get patientHasDateOfBirth(): boolean {
    const patient = this.patientService.patient;
    return !!patient && !!patient.dateOfBirth;
  }

  public get isPatientEnrolledInPatientApp(): boolean {
    const patient = this.patientService.patient;
    const enrollmentConfig = patient && patient.patientAppEnrollmentConfig ? safeJsonParse(patient.patientAppEnrollmentConfig) : null;
    const status = enrollmentConfig && enrollmentConfig.status ? enrollmentConfig.status.toLowerCase() : '';
    return !!patient && !!patient.patientAppId && status !== 'paused';
  }

  public get isPatientReEnrollEligible(): boolean {
    const patient = this.patientService.patient;
    return isReEnrollmentEligible(patient);
  }

  public onGeneratePdf(exportRequest: {
    pdfType: PDFType;
    lang: string;
    destination: ExportDestination;
  }): void {
    if (exportRequest.destination === ExportDestinations.Download) {
      this.downloadAssessmentPdf(exportRequest.pdfType, exportRequest.lang);
      return;
    }
    this.sendAssessmentToPatient(
      exportRequest.pdfType,
      exportRequest.lang,
      exportRequest.destination
    );
  }

  private sendAssessmentToPatient(
    pdfType: PDFType,
    lang: string,
    destination: ExportDestination
  ): void {
    const patient = this.patientService.patient;
    const assessmentBody = safeJsonParse(this.assessmentRow.assessment.body) as AssessmentBody;
    if (!patient || !assessmentBody) {
      return;
    }

    this.assessmentPdfSendService
      .confirmSend(patient, destination)
      .pipe(untilDestroyed(this))
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }
        this.assessmentPdfSendService.runSend({
          assessmentBody: assessmentBody,
          clinic: this.clinicSetupService.clinic,
          clinicName: this.clinicSetupService.clinic.name,
          clinicId: this.clinicSetupService.clinic.id,
          assessmentId: this.assessmentRow.assessment.id,
          reportType: this.assessmentRow.methodLabel,
          patient: patient,
          pdfType,
          lang,
          destination
        });
      });
  }

  private downloadAssessmentPdf(pdfType: PDFType, lang: string): void {
    const assessmentBody = safeJsonParse(this.assessmentRow.assessment.body) as AssessmentBody;
    if (!assessmentBody) {
      return;
    }
    this.assessmentPdfSendService.runDownload(
      assessmentBody,
      this.clinicSetupService.clinic,
      pdfType,
      lang
    );
  }

  public ngOnDestroy(): void {}

  public close(): void {
    this.sheetReference.close();
  }

  public openAssessment(): void {
    this.sheetReference.close('openAssessment');
  }

  public toggleCompare(): void {
    this.sheetReference.close('compare');
  }

  public trackByPlanLabel(index: number, planRow: { label: string }): string {
    return planRow.label;
  }

  public trackByInstrumentName(index: number, scoreChip: { instrumentName: string }): string {
    return scoreChip.instrumentName;
  }

  public trackByQuestionnaireId(index: number, linkedRow: LinkedQuestionnaireRow): string {
    return linkedRow && linkedRow.questionnaireId ? linkedRow.questionnaireId : String(index);
  }
}
